import { useEffect, useRef, useState } from 'react';
import { RotateCcw, X } from 'lucide-react';
import { queueActivity } from '../lib/activityIntelligence';
import {
  RESUME_NOTICE_EVENT,
  emitResumeNoticeAction,
} from '../lib/resumeNotice';

const GlobalResumeNotice = () => {
  const [notice, setNotice] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = null;
    };
    const handleNotice = (event) => {
      const next = event.detail;
      if (!next?.id) return;
      clearTimer();
      setNotice(next);
      queueActivity({
        activity_type: 'resume_notice_shown',
        entity: next.entity,
        source: 'resume-ux',
        metadata: { kind: next.kind, position: next.position, unit: next.unit, anchor: next.anchor },
      });
      timerRef.current = window.setTimeout(() => setNotice(null), next.durationMs || 5000);
    };
    window.addEventListener(RESUME_NOTICE_EVENT, handleNotice);
    return () => {
      clearTimer();
      window.removeEventListener(RESUME_NOTICE_EVENT, handleNotice);
    };
  }, []);

  if (!notice) return null;

  const dismiss = () => {
    queueActivity({ activity_type: 'resume_notice_dismissed', entity: notice.entity, source: 'resume-ux' });
    setNotice(null);
  };
  const startOver = () => {
    emitResumeNoticeAction({ notice, action: 'start-over' });
    queueActivity({ activity_type: 'resume_start_over', entity: notice.entity, source: 'resume-ux' });
    setNotice(null);
  };

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 bottom-24 z-[90] w-[calc(100%-2rem)] max-w-md border border-white/15 bg-black/95 shadow-2xl backdrop-blur px-4 py-3 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2"
      role="status"
      aria-live="polite"
      data-testid="global-resume-notice"
    >
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.22em] text-primary font-bold">Continuity</p>
          <p className="text-sm text-white mt-0.5 truncate">{notice.message}</p>
        </div>
        {notice.canStartOver && (
          <button
            type="button"
            onClick={startOver}
            className="shrink-0 inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-primary transition-colors px-2 py-2"
            data-testid="resume-start-over"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Start Over
          </button>
        )}
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 text-white/40 hover:text-white transition-colors p-1"
          aria-label="Dismiss resume notice"
          data-testid="resume-notice-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default GlobalResumeNotice;
